import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'

export interface ClientPaymentStats {
  activeProjects: number
  totalProjects: number
  totalPaid: number
  totalPending: number
  totalOverdue: number
  paymentsCount: number
}

// Buscar stats de todos os clientes baseado em pagamentos
export function useAllClientPaymentStats() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['clients', 'payment-stats', 'all'],
    queryFn: async () => {
      // Buscar projetos com client_id
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('id, client_id, status')

      if (projectsError) throw projectsError

      // Buscar todos os pagamentos
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('project_id, amount, status')

      if (paymentsError) throw paymentsError

      // Mapear project_id para client_id
      const projectClientMap = new Map<string, string>()
      const clientProjectsMap = new Map<string, { active: number; total: number }>()

      projects?.forEach(p => {
        if (p.client_id) {
          projectClientMap.set(p.id, p.client_id)
          
          if (!clientProjectsMap.has(p.client_id)) {
            clientProjectsMap.set(p.client_id, { active: 0, total: 0 })
          }
          
          const stats = clientProjectsMap.get(p.client_id)!
          stats.total += 1
          if (p.status !== 'done') {
            stats.active += 1
          }
        }
      })

      // Agregar pagamentos por cliente
      const clientStatsMap: Record<string, ClientPaymentStats> = {}

      // Inicializar stats com dados de projetos
      clientProjectsMap.forEach((projectStats, clientId) => {
        clientStatsMap[clientId] = {
          activeProjects: projectStats.active,
          totalProjects: projectStats.total,
          totalPaid: 0,
          totalPending: 0,
          totalOverdue: 0,
          paymentsCount: 0,
        }
      })

      // Agregar pagamentos
      payments?.forEach(payment => {
        const clientId = payment.project_id ? projectClientMap.get(payment.project_id) : null
        if (!clientId) return

        if (!clientStatsMap[clientId]) {
          clientStatsMap[clientId] = {
            activeProjects: 0,
            totalProjects: 0,
            totalPaid: 0,
            totalPending: 0,
            totalOverdue: 0,
            paymentsCount: 0,
          }
        }

        const amount = Number(payment.amount) || 0
        clientStatsMap[clientId].paymentsCount += 1

        switch (payment.status) {
          case 'paid':
            clientStatsMap[clientId].totalPaid += amount
            break
          case 'pending':
            clientStatsMap[clientId].totalPending += amount
            break
          case 'overdue':
            clientStatsMap[clientId].totalOverdue += amount
            break
        }
      })

      return clientStatsMap
    },
    enabled: !!user,
  })
}

// Buscar stats de um cliente específico baseado em pagamentos
export function useClientPaymentStats(clientId: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['clients', clientId, 'payment-stats'],
    queryFn: async () => {
      // Buscar projetos do cliente
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('id, status')
        .eq('client_id', clientId)

      if (projectsError) throw projectsError

      const projectIds = projects?.map(p => p.id) || []
      const activeProjects = projects?.filter(p => p.status !== 'done').length || 0
      const totalProjects = projects?.length || 0

      if (projectIds.length === 0) {
        return {
          activeProjects: 0,
          totalProjects: 0,
          totalPaid: 0,
          totalPending: 0,
          totalOverdue: 0,
          paymentsCount: 0,
        } as ClientPaymentStats
      }

      // Buscar pagamentos dos projetos do cliente
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('amount, status')
        .in('project_id', projectIds)

      if (paymentsError) throw paymentsError

      let totalPaid = 0
      let totalPending = 0
      let totalOverdue = 0

      payments?.forEach(payment => {
        const amount = Number(payment.amount) || 0
        switch (payment.status) {
          case 'paid':
            totalPaid += amount
            break
          case 'pending':
            totalPending += amount
            break
          case 'overdue':
            totalOverdue += amount
            break
        }
      })

      return {
        activeProjects,
        totalProjects,
        totalPaid,
        totalPending,
        totalOverdue,
        paymentsCount: payments?.length || 0,
      } as ClientPaymentStats
    },
    enabled: !!user && !!clientId,
  })
}
